import { OrderByType } from '@loop/ui'
import { Box, Divider, Typography } from '@mui/material'
import { capitalize, isEqual } from 'lodash'
import get from 'lodash/get'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import CustomMaterialTableWrapper from 'src/components/CustomMaterialTableWrapper/CustomMaterialTableWrapper'
import OrderIdCompClickable from 'src/components/OrderComponentsOld/OrderIdCompClickable'
import OrderDrawer from 'src/components/OrderDrawer/OrderDrawer'
import PlatformLogo from 'src/components/PlatformLogo'
import ColoredTooltip from 'src/components/Tooltip/ColoredTooltip'
import { useErrorData } from 'src/context/ErrorContext'
import { useFilter } from 'src/context/FilterContext'
import { DefaultService, DelIntelTablePaginated, DelIntelThemeGroupby } from 'src/services/openApiV2'
import { filterNames } from 'src/utils/config/config'
import { formatCurrency } from 'src/utils/functions'
import ErrorTagChip from '../ErrorTagChip'
import ErrorThemeChip from '../ErrorThemeChip'
import { ProblematicType } from './ErrorInsightsThemeDrawer'

interface ErrorInsightsThemeTableProps {
  errorTheme: DelIntelThemeGroupby
  activeProblematic: { type: ProblematicType; name: string }
}

const ErrorInsightsThemeTable = ({ errorTheme, activeProblematic }: ErrorInsightsThemeTableProps) => {
  const { getFilters, getFiltersV2 } = useFilter()
  const { handleError } = useErrorData()

  const [orderReviewData, setOrderReviewData] = useState<DelIntelTablePaginated>(null)
  const [orderReviewDataLoading, setOrderReviewDataLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState<{
    order_id: string
    order_date: string
    slug: string
    theme: string
  }>(null)
  const [searchValue, setSearchValue] = useState<string>(null)
  const [sortBy, setSortBy] = useState<string>('error_charge')
  const [sortOrder, setSortOrder] = useState<OrderByType>('descending')

  const filterArr = ['b_name', 'vb_name', 'vb_platform', 'am_name', 'chain', 'start_date', 'end_date']

  const filterObj = useMemo(() => {
    return getFiltersV2(filterArr, true)
  }, [...getFilters(filterArr)])

  const getOrderReviewData = async (body: { limit?: number; offset?: number }, callback: (data: DelIntelTablePaginated) => void) => {
    setOrderReviewDataLoading(true)
    try {
      const limit = get(body, 'limit', 100)
      const offset = get(body, 'offset', 0)

      const res = await DefaultService.callDelIntelDelIntelTableApiPaginatedDelIntelDelIntelTablePost({
        requestBody: {
          ...filterObj,
          limit: limit,
          offset: offset,
          theme_in: errorTheme?.theme,
          search_term: searchValue,
          order_by: sortBy,
          ascending: sortOrder === 'ascending'
        }
      })

      callback(res)
    } catch (err) {
      handleError(err?.message)
    }
    setOrderReviewDataLoading(false)
  }

  const onLastPage = () => {
    const next_offset = get(orderReviewData, 'next_offset', null)
    if (next_offset) {
      getOrderReviewData(
        {
          limit: 100,
          offset: next_offset
        },
        (res) => {
          setOrderReviewData((prev) => {
            return {
              ...res,
              data: [...get(prev, 'data', []), ...get(res, 'data', [])]
            }
          })
        }
      )
    }
  }

  const getCSVString = async () => {
    try {
      const res = (await DefaultService.exportDelIntelDelIntelTableApiExportDelIntelDelIntelTablePost({
        requestBody: {
          ...filterObj,
          theme_in: errorTheme?.theme,
          search_term: searchValue
        }
      })) as any

      return res
    } catch (err) {
      handleError(err?.message)
    }
  }

  useEffect(() => {
    if (!filterObj) return

    getOrderReviewData({ limit: 100, offset: 0 }, (data) => {
      setOrderReviewData(data)
    })
  }, [filterObj, searchValue, sortBy, sortOrder])

  useEffect(() => {
    if (!activeProblematic?.name) {
      setSearchValue(null)
    } else {
      setSearchValue(activeProblematic?.name)
    }
  }, [activeProblematic?.name])

  return (
    <>
      <OrderDrawer
        orderDetails={{
          order_id: selectedOrder?.order_id,
          open: !!selectedOrder?.order_id,
          slug: selectedOrder?.slug,
          order_date: selectedOrder?.order_date,
          theme: selectedOrder?.theme
        }}
        onClose={() => setSelectedOrder(null)}
      />

      <CustomMaterialTableWrapper
        isLoading={orderReviewDataLoading}
        data={get(orderReviewData, 'data', [])}
        columns={[
          {
            title: 'Order',
            render: (data) => (
              <Box
                display="flex"
                flexDirection="column"
                gap={1}>
                <OrderIdCompClickable
                  platform={get(data, 'vb_platform', '')}
                  orderId={get(data, 'order_id', '')}
                />
                <Typography
                  variant="subtitle2"
                  color="#0008">
                  {!data.order_timestamp || get(data, 'order_timestamp', '').toString().toLowerCase() === ''
                    ? '-'
                    : moment(data['order_timestamp']).format('DD MMM YYYY hh:mm A')}
                </Typography>
              </Box>
            ),
            field: 'order_timestamp'
          },
          {
            title: filterNames.b_name,
            field: 'b_name',
            render: (data) => {
              return (
                <Typography
                  fontSize={12}
                  color="#000"
                  fontWeight={600}>
                  {get(data, 'b_name', null)}
                </Typography>
              )
            }
          },
          {
            title: 'Item',
            field: 'item_name',
            render: (data) => {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.5}>
                  <Typography
                    fontSize={12}
                    color="#000">
                    {get(data, 'item_name', null)}
                  </Typography>

                  {!isEqual(get(data, 'item_name', '').replace(/\s+/g, ''), get(data, 'concerned_item', '').replace(/\s+/g, '')) && (
                    <>
                      <Divider />
                      <Typography
                        fontSize={12}
                        color="#000A">
                        {get(data, 'concerned_item', null)}
                      </Typography>
                    </>
                  )}
                </Box>
              )
            }
          },

          {
            title: 'Comment',
            field: 'customer_comments',
            sortable: false,
            render: (data) => {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  maxWidth="300px">
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}>
                      <PlatformLogo
                        platformName={get(data, 'vb_platform', null)}
                        shortImg
                        height="18px"
                        width="18px"
                        boxSx={{
                          flex: 'unset',
                          display: 'flex'
                        }}
                      />

                      {get(data, 'customer_name', null) && (
                        <Typography
                          fontSize={12}
                          color="#000"
                          lineHeight="initial"
                          maxWidth="150px"
                          noWrap>
                          {get(data, 'customer_name', '')}
                        </Typography>
                      )}
                    </Box>

                    {get(data, 'customer_comments', null) && (
                      <>
                        <Divider />
                        <ColoredTooltip title={get(data, 'customer_comments', '').length > 50 ? get(data, 'customer_comments', '') : null}>
                          <Box>
                            <Typography
                              fontSize={12}
                              color="#000"
                              fontWeight={600}
                              lineHeight="initial"
                              sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                boxOrient: 'vertical',
                                WebkitLineClamp: 3,
                                lineClamp: 3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                              "{get(data, 'customer_comments', null)}"
                            </Typography>
                          </Box>
                        </ColoredTooltip>
                      </>
                    )}
                  </Box>
                </Box>
              )
            }
          },
          {
            title: 'Tags',
            field: 'tags',
            sortable: false,
            render: (data) => {
              if (!get(data, 'tags', null) && !get(data, 'theme', null)) return null

              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}>
                  <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap">
                    {get(data, 'theme', null) && <ErrorThemeChip theme={get(data, 'theme', null)} />}
                  </Box>
                  <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap">
                    {get(data, 'tags', []).map(
                      (tag) =>
                        tag && (
                          <ErrorTagChip
                            key={tag}
                            tag={capitalize(tag)}
                          />
                        )
                    )}
                  </Box>
                </Box>
              )
            }
          },
          {
            title: 'Error Charge',
            field: 'error_charge',
            render: (data) => {
              return (
                <Typography
                  fontSize={12}
                  color="#E52E2E">
                  {formatCurrency(get(data, 'error_charge', null))}
                </Typography>
              )
            }
          }
        ]}
        options={{
          search: true,
          export: true,
          pagination: true,
          sortable: true,
          initialOrderBy: sortOrder,
          initialSortableOption: sortBy,
          totalPaginatedDataLength: get(orderReviewData, 'max_rows', 0),
          debounceMilliseconds: 1500,
          searchPlaceholder: 'Search by orders'
        }}
        onLastPage={onLastPage}
        onSortByChange={(option) => setSortBy(option.value)}
        onOrderByChange={(option) => setSortOrder(option)}
        onExportModifyCSV={getCSVString}
        onSearchChange={(val) => {
          if (val) {
            setSearchValue(val)
          } else setSearchValue(null)
        }}
        onRowClick={(data) =>
          setSelectedOrder({
            order_id: get(data, 'order_id', null),
            order_date: get(data, 'order_timestamp', null),
            slug: get(data, 'slug', null),
            theme: get(data, 'theme', null)
          })
        }
      />
    </>
  )
}

export default ErrorInsightsThemeTable
